import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const statusStyles = {
  active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  suspended: 'border-amber-300 bg-amber-50 text-amber-800',
  revoked: 'border-red-300 bg-red-50 text-red-700',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function IdentityStatus({ status }) {
  const label = String(status || 'active').replaceAll('_', ' ');
  return (
    <span className={`inline-flex items-center gap-1.5 border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${statusStyles[status] || statusStyles.active}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70"/>
      {label}
    </span>
  );
}

export default function IdentityOperations() {
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/idem-identities/admin/identities');
      setIdentities(data.identities || []);
    } catch (requestError) {
      setError(requestError?.response?.status === 403
        ? 'This internal page is not available for this account.'
        : 'Identity Operations could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="identity-operations-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Identity Operations" showDashboard={false}/>

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Platform</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Identity Operations</h1>
        <p className="mt-2 text-sm text-zinc-600">Internal identity management for idem.</p>
      </div>

      <div className="border border-zinc-200 bg-white">
        {loading ? (
          <div className="px-4 py-8 text-sm text-zinc-500">Loading identities…</div>
        ) : error ? (
          <div className="px-4 py-8 text-sm text-red-700">{error}</div>
        ) : identities.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-zinc-500">No idem identities have been created yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-zinc-200 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Identity ref</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Source verification</th>
                  <th className="px-4 py-3 font-medium">Verified at</th>
                  <th className="px-4 py-3 font-medium">Created at</th>
                </tr>
              </thead>
              <tbody>
                {identities.map((identity) => (
                  <tr key={identity.id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-700">
                      <Link to={`/internal/identities/${identity.public_id || identity.id}`} className="hover:text-black hover:underline">{identity.public_id}</Link>
                    </td>
                    <td className="px-4 py-4 align-middle"><IdentityStatus status={identity.status}/></td>
                    <td className="px-4 py-4 align-middle text-zinc-800">{identity.display_name || '—'}</td>
                    <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-600">{identity.primary_email || '—'}</td>
                    <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-600">
                      {identity.source_verification_id ? (
                        <Link to={`/dashboard/verifications/${identity.source_verification_id}`} className="hover:text-black hover:underline">
                          {identity.source_verification_id}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-4 align-middle text-xs text-zinc-600">{formatDate(identity.verified_at)}</td>
                    <td className="px-4 py-4 align-middle text-xs text-zinc-600">{formatDate(identity.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
